
import { useState } from "react";

const typeDetailsData = {
  assets: {
    title: "English",
    description: "Assets Details",
    content: `
By reading this guide, you can easily understand and use this software step by step. This guide is written in simple language, so even users without accounting knowledge can confidently manage accounts, inventory, and daily transactions.

Part 1: What is an Account ?

An account is a place where you record money coming in or going out. To keep things simple, all accounts are divided into 4 main types:

Type           / Meaning (Simple Words)
Assets         / What you own
Liabilities    / What you owe
Income         / Money you earn
Expense        / Money you spend  

Part 2: Account Structure (step by step)

Account structure flow:

Account Type  
   └── Group  
        └── Sub-Group  
              └── Ledger (Actual Account)

Example:

Assets
 └── Current Assets
      └── Cash & Bank
           └── Cash-in-Hand

 Part 3: Which ledger should come under which type?

This section helps you decide: 
• Which Account Type to use 
• Which Group / Sub-Group to create  
• Which Ledger name to add

🟢 Assets (What  You Own)

Assets are things that belong to your business or money that you will receive.

1️⃣ Current Assets 

(Things that change daily)

🔹 Cash & Bank

Ledgers you can create: 
• Cash-in-Hand 
• SBI Bank 
• Current Account 
• Savings Account 
• UPI  Account

🔹 Sundry Debtors (Customers)

Ledgers you can create:
• Customer – Ram Traders 

✔ Anyone who buys from you and pays later

🔹 Stock / Inventory

Ledgers you can create:
• Closing Stock 
• Opening Stock

2️⃣ Fixed Assets

(Things used for a long time)

Ledgers you can create:
• Building 
• Machinery 
• Delivery Vehicle 

🔴 Liabilities (What you owe)

Liabilities are amounts you have to pay to someone else.

1️⃣ Capital Account

(Money invested by owner)

Ledgers you can create:
• Owner Capital 
• Partner Capital

2️⃣ Current Liabilities

(Short-term dues)

🔹 Sundry Creditors (Suppliers)

Ledgers you can create:
• Supplier – Gupta Traders 
• Supplier – Steel World 

✔ Anyone from whom you buy goods and pay later

 3️⃣ Loans (Borrowed Money)

Ledgers you can create:
• SBI Loan 

🔵 Income (Money you earn)

Income is money coming into your business.

2️⃣ Direct Income

(Main business income)

Ledgers you can create:
• Sales Account 
• Sales – Cement 
• Sales – Grocery 


 3️⃣ Indirect Income

(Other income)

Ledgers you can create:
• Interest Received 
• Commission Received 
• Discount Received 
• Rental Income

🟡 Expense (Money you spend)

Expense is money going out from your business.

1️⃣ Direct Expense

(Expenses related to buying goods)

Ledgers you can create:
• Purchase Account 
• Purchase – Cement 

2️⃣ Indirect Expense

(Daily running expense)

🔹 Office & General Expense

Ledgers you can create:

• Rent Expense 
• Electricity Expense 

🔹 Salary & Wages

Ledgers you can create:

• Salary Expense 
🔹 Transportation
  
Ledgers you can create:

  • Transportation Expense  

🔹  Other 

Ledgers you can create:

• Repair & Maintenance 
• Advertisement Expense 
• Commission Paid 
• Discount Allowed

Part 4: How to create Inventory (Items) ? These items will be used during Sale & Purchase entries.

Step 1: Create Categories

Examples: 
• Cement 
• Grocery 
• Electronics

Step 2: Add Items under Categories

Examples: Cement → Ultratech Cement

Grocery → Sugar, Rice

🔹 Part 5: Payment types setup 

Q. What is Payment Type?

Payment Type means how you receive or pay money.

Examples:
• Cash 
• SBI Bank 
• UPI

Q. How to Create Payment Type ?

1. Go to Settings → Payment Types

2. Create Payment Type

3. Connect it with a Ledger

Example Mapping :
 Payment Type /Connected Ledger 
 Cash	/ Cash-in-Hand 
 SBI	 / SBI Bank 
 UPI /	HDFC Bank

✔ If you have 2 bank accounts, create 2 payment types.
✔ If you want to set a payment type as default during entry, you need to select it in the Payment Table. The selected payment type will automatically appear as the default in your entries.

🔹 Part 5.5: Show Transportation & Loading in Sales & Purchase

If you want to add transportation or loading charges directly inside your sale or purchase entries, you need to enable this setting first.

👉 Go to Settings → Show Transportation & Loading in Sales & Purchase


✔ Once enabled, transport and loading fields will automatically appear in every Sale & Purchase entry.

🔹 Part 6: Transportation Area & Fare

If you deliver goods to different places and have fixed fare for different areas. And you want to add that fare in the transportation expense account then you need to add transportation areas and its fare.

Step 1: Create Transportation Area

Examples: City A , City B

Step 2: Add Fare for Each Area

Example: City A → ₹500 , City B → ₹800

Q. How it Works

When you sell items and select transportation area:
✔ Transport fare is automatically added
✔ Amount goes to Transport Expense Ledger

🔹 Part 6.5: Entry Filter Setup (Search Filter in Voucher Inputs)

When you create a Sale, Purchase, Receipt, Payment or Journal entry, each input field has a Search Filter option. Using this, you can control which Groups and Sub-Groups appear as suggestions in that input.

👉 Open any Voucher entry → Click the Search Filter icon on any input field → Select the Group or Sub-Group you want to show

This is useful because:
✔ In Sale entry → Show only Sundry Debtors (Customers)
✔ In Purchase entry → Show only Sundry Creditors (Suppliers)
✔ In Receipt entry → Show Debtors + Cash & Bank
✔ In Payment entry → Show Creditors + Cash & Bank
✔ In Journal entry → Show all groups as needed

Voucher Type / Recommended Filter
 Sale  / Sundry Debtors (Customers)
 Purchase  / Sundry Creditors (Suppliers)
 Receipt  / Debtors + Cash & Bank
 Journal  / All Groups as needed
 payment /  Creditors + Cash & Bank

✔ This helps you find the right ledger quickly without scrolling through all accounts!

🔹 Part 6.8: Journal Entry Item Settings

If you want to show Items, Manage Stock, Quantity and Rate fields in Journal Entry:

👉 Go to Settings → More Options → Enable "Show Items, Manage Stock & Quantity in Journal Entry"

✔ This allows inventory management within Journal Vouchers.

🔹 Part 7: Voucher Entries (Daily Work)

📑 Receipt

Used when you receive money 
Example: Customer pays cash

💸 Payment

Used when you pay money
Example:
• Rent paid 
• Electricity bill paid

🛒 Sale

Used when you sell items
Example: Sale of cement to customer

📦 Purchase

Used when you buy items
Example: Purchase of grocery stock

📘 Journal

Used for adjustments and entries where cash or bank is not involved.
Example:
• Opening balance 
• Transfer between accounts

🔹 Part 8: Manager

You can create managers to handle your business, assign them specific branches and rights to define what they can use in the software.

Example:
1. Create a Manager "A".
2. Assign Branch: "ABC Company".
3. Assign Rights: Entry, Day Book, Account Book.

Result:
When Manager "A" logs in, they will only see and use the Entry, Day Book, and Account Book sections. All other features will be hidden/restricted.

👉 Go to Settings → Manage Users / Managers → Create Manager
`
  },
  hindi: {
    title: "हिंदी",
    description: "संपूर्ण लेखा मार्गदर्शिका",
    content: `
इस जानकारी को पढ़कर आप इस सॉफ़्टवेयर को आसानी से समझ और उपयोग कर सकते हैं। यह गाइड सरल भाषा में लिखी गई है, ताकि अकाउंटिंग ज्ञान न होने पर भी आप खातों, इन्वेंटरी और रोज़ाना लेन-देन को सही तरीके से संभाल सकें।

भाग 1.खाता क्या है?

खाता वह जगह है जहां आप आने वाले या जाने वाले पैसे को रिकॉर्ड करते हैं। सरल बनाने के लिए, सभी खातों को 4 मुख्य प्रकारों में विभाजित किया गया है:

प्रकार        / अर्थ 
संपत्ति       / जो आपके पास है (सम्पत्ति,लेनदारी )
दायित्व       / जो आप पर कर्ज है (दायित्व, देनदारी)
कमाई            / जो आप कमाते हैं (कमाई, बिक्री)
खर्चा          / जो आप खर्च करते हैं (खर्चा, खरीद)

भाग 2: खाता संरचना (एक-एक चरण में)

खाता संरचना प्रवाह:
खाता प्रकार
   └── समूह
        └── उप-समूह
              └── खाता बही (वास्तविक खाता)

उदाहरण:
संपत्ति
 └── चालू संपत्ति
      └── नकद और बैंक
           └── हाथ में नकद

भाग 3: कौन सा बही खाता किस प्रकार (टाइप) के अंतर्गत आना चाहिए?

यह अनुभाग आपको तय करने में मदद करता है कि :
• बही खाता किस प्रकार (टाइप)  के अंतर्गत  आयेगा |
• कौन सा समूह / उप-समूह बनाना है |
• कौन सा बही खाता  नाम जोड़ना है |

🟢 संपत्ति (आपकी संपत्ति)

संपत्ति वे चीजें हैं जो आपके व्यवसाय की हैं या वह पैसा जो आपको मिलेगा।

1️⃣ चालू संपत्ति
  चीजें जो रोजाना बदलती हैं|

🔹 नकद और बैंक
 इसके अंतर्गत आप निम्न बही खाते बना सकते हैं:
• हाथ में नकद
• एसबीआई बैंक
• चालू खाता
• बचत खाता
• यूपीआई खाता

🔹 अन्य देनदारी (ग्राहक)
इसके अंतर्गत आप निम्न बही खाते बना सकते हैं:
• ग्राहक – राम ट्रेडर्स

✔ कोई भी जो आपसे खरीदता है और बाद में भुगतान करता है|

🔹 स्टॉक / इन्वेंटरी
इसके अंतर्गत आप निम्न बही खाते बना सकते हैं:
• क्लोज़िंग स्टॉक
• ओपनिंग स्टॉक

2️⃣ स्थायी संपत्तियाँ
  लंबे समय तक उपयोग की जाने वाली चीजें, इसके अंतर्गत आप निम्न बही खाते बना सकते हैं:
• इमारत
• कार्यालय (ऑफिस) फर्नीचर
• मशीनरी
• डिलीवरी वाहन

🔴 दायित्व (आपके देयताएँ, ऋण)

  दायित्व वे राशियां हैं जो आपको किसी और को देनी हैं।

1️⃣ पूंजी खाता
  मालिक द्वारा निवेश किया गया पैसा, इसके अंतर्गत आप निम्न बही खाते बना सकते हैं:
• मालिक पूंजी
• साझेदार पूंजी

2️⃣ चालू देयताएं
  थोड़े समय क लिए देय राशि |

🔹 अन्य लेनदारी (आपूर्तिकर्ता)
इसके अंतर्गत आप निम्न बही खाते बना सकते हैं:
• आपूर्तिकर्ता – गुप्ता ट्रेडर्स
• आपूर्तिकर्ता – स्टील वर्ल्ड
✔ कोई भी जिससे आप सामान खरीदते हैं और बाद में भुगतान करते हैं|

🔹 भुगतान योग्य व्यय (खर्च)
इसके अंतर्गत आप निम्न बही खाते बना सकते हैं:

3️⃣ लोन (उधार लिया गया पैसा)
इसके अंतर्गत आप जो बही खाते बना सकते हैं:
• एसबीआई लोन

🔵 आय (कमाई)  
  आय वह पैसा है जो आप व्यवसाय से कमाते है|

1️⃣ प्रत्यक्ष आय
  जो आय हमे व्यवसाय से मिलती है इसके अंतर्गत आप निम्न बही खाते बना सकते हैं:
• बिक्री खाता
• सीमेंट - बिक्री
• किराना - बिक्री

2️⃣ अप्रत्यक्ष आय
  अन्य आय , इसके अंतर्गत आप निम्न बही खाते बना सकते हैं:
• ब्याज प्राप्त
• कमीशन प्राप्त
• छूट प्राप्त
• किराये की आय

🟡 व्यय (खर्चा)

  व्यय वह पैसा है जो आप व्यवसाय के लिए खर्च करते हैं|

1️⃣ प्रत्यक्ष व्यय
  सामान खरीदने से संबंधित व्यय, इसके अंतर्गत आप निम्न बही खाते बना सकते हैं:
• खरीद खाता
• सीमेंट - खरीद

2️⃣ अप्रत्यक्ष व्यय
  दैनिक चलने वाले व्यय 

🔹 कार्यालय और सामान्य व्यय
  इसके अंतर्गत आप निम्न बही खाते बना सकते हैं:
• किराया 
• बिजली खर्च

🔹 वेतन और मजदूरी
इसके अंतर्गत आप निम्न बही खाते बना सकते हैं:
• वेतन (सैलरी) खर्च

🔹 परिवहन व्यय
इसके अंतर्गत आप निम्न बही खाते बना सकते हैं:
• परिवहन खर्च

🔹 अन्य व्यय
इसके अंतर्गत आप निम्न बही खाते बना सकते हैं:
• मरम्मत और रखरखाव
• विज्ञापन व्यय
• कमीशन भुगतान
• छूट 

भाग 4: इन्वेंटरी (आइटम) कैसे बनाएं | ये आइटम बिक्री और खरीद प्रविष्टियों के दौरान उपयोग किए जाएंगे।

चरण 1: कैटेगरी बनाएं
उदाहरण:
• सीमेंट
• किराना
• इलेक्ट्रॉनिक्स

चरण 2: कैटेगरी के अंतर्गत आइटम जोड़ें|
उदाहरण:
• सीमेंट → अल्ट्राटेक सीमेंट
• किराना → चीनी, चावल

🔹 भाग 5: भुगतान प्रकार सेटअप (बहुत महत्वपूर्ण)

Q. भुगतान प्रकार क्या है ? 
  भुगतान प्रकार का मतलब है कि आप पैसे कैसे प्राप्त करते हैं या भुगतान करते हैं।

उदाहरण:
• नकद
• एसबीआई बैंक
• यु पी आई 

Q. भुगतान प्रकार कैसे बनाएं ?
1. सेटिंग्स → भुगतान प्रकार पर जाएं|
2. भुगतान प्रकार बनाएं|
3. इसे एक खाता बही से कनेक्ट करें|

उदाहरण मैपिंग:
भुगतान प्रकार  / जुड़ा हुआ खाता बही
नकद            / हाथ में नकद
एसबीआई        / एसबीआई बैंक
यु पी आई      / एचडीएफसी बैंक

✔ यदि आपके पास 2 बैंक खाते हैं, तो 2 भुगतान प्रकार बनाएं।
✔ यदि आप एंट्री के समय किसी भुगतान प्रकार को डिफ़ॉल्ट रखना चाहते हैं, तो आपको उसे पेमेंट टेबल में चुनना होगा। चयनित भुगतान प्रकार आपकी एंट्री में स्वतः डिफ़ॉल्ट रूप से लागू हो जाएगा।

🔹 भाग 5.5: बिक्री और खरीद में परिवहन और लोडिंग दिखाएं

यदि आप अपनी बिक्री या खरीद एंट्री में सीधे परिवहन या लोडिंग शुल्क जोड़ना चाहते हैं, तो पहले यह सेटिंग चालू करनी होगी।

👉 सेटिंग्स → बिक्री और खरीद में परिवहन और लोडिंग दिखाएं पर जाएं

✔ एक बार चालू होने पर, परिवहन और लोडिंग फील्ड हर बिक्री और खरीद एंट्री में अपने आप दिखेंगी।

भाग 6: परिवहन क्षेत्र और किराया |

यदि माल अलग-अलग क्षेत्रों में भेजा जाता है और हर क्षेत्र के लिए भाड़ा तय है, तो उस भाड़े को परिवहन व्यय खाते में जोड़ने के लिए परिवहन क्षेत्र और भाड़ा दर्ज करिये:

चरण 1: परिवहन क्षेत्र बनाएं|
उदाहरण:
• शहर ए
• शहर बी

चरण 2: प्रत्येक क्षेत्र के लिए किराया जोड़ें |
उदाहरण:
• शहर ए → ₹500
• शहर बी → ₹800

Q. यह कैसे काम करता है ?
   जब आप आइटम बेचते हैं और परिवहन क्षेत्र का चयन करते हैं:
✔ परिवहन किराया स्वचालित रूप से जोड़ा जाता है|
✔ राशि परिवहन व्यय बही खाता  में जाती है|

🔹 भाग 6.5: एंट्री फ़िल्टर सेटअप (वाउचर इनपुट में सर्च फ़िल्टर)

जब आप बिक्री, खरीद, रसीद, भुगतान या जर्नल एंट्री बनाते हैं, तो हर इनपुट फील्ड पर एक सर्च फ़िल्टर का विकल्प होता है। इसका उपयोग करके आप नियंत्रित कर सकते हैं कि उस इनपुट में कौन से ग्रुप और सब-ग्रुप सुझाव के रूप में दिखें।

👉 कोई भी वाउचर एंट्री खोलें → किसी भी इनपुट फील्ड पर सर्च फ़िल्टर आइकन पर क्लिक करें → जो ग्रुप या सब-ग्रुप दिखाना हो उसे चुनें

यह उपयोगी है क्योंकि:
✔ बिक्री एंट्री में → केवल  लेनदारी (ग्राहक) दिखाएं
✔ खरीद एंट्री में → केवल  देनदारी (आपूर्तिकर्ता) दिखाएं
✔ रसीद एंट्री में → देनदार + नकद और बैंक दिखाएं
✔ भुगतान एंट्री में → लेनदार + नकद और बैंक दिखाएं
✔ जर्नल एंट्री में → आवश्यकतानुसार सभी ग्रुप दिखाएं

वाउचर प्रकार /  अनुशंसित फ़िल्टर
 बिक्री /  लेनदारी (ग्राहक)
 खरीद /  देनदारी (आपूर्तिकर्ता)
 रसीद / देनदार + नकद और बैंक
 भुगतान / लेनदार + नकद और बैंक
 जर्नल / आवश्यकतानुसार सभी ग्रुप

✔ इससे सही बही खाता जल्दी मिलता है — सभी खातों में स्क्रॉल नहीं करना पड़ता!

🔹 भाग 6.8: जर्नल एंट्री आइटम सेटिंग्स

यदि आप जर्नल एंट्री में आइटम, स्टॉक मैनेजमेंट, मात्रा और रेट फील्ड दिखाना चाहते हैं:

👉 सेटिंग्स → अधिक विकल्प (More Options) → "जर्नल एंट्री में आइटम, स्टॉक और मात्रा दिखाएं" को सक्षम करें।

✔ यह जर्नल वाउचर के भीतर इन्वेंट्री प्रबंधन की अनुमति देता है।

 भाग 7: वाउचर एंट्री (दैनिक कार्य) 

📑 रसीद
  जब आप पैसे प्राप्त करते हैं तो उपयोग किया जाता है|
उदाहरण:
• ग्राहक नकद भुगतान करता है|

💸 भुगतान
  जब आप पैसे का भुगतान करते हैं तो उपयोग किया जाता है|
उदाहरण:
• किराया भुगतान
• बिजली बिल भुगतान

🛒 बिक्री
  जब आप आइटम बेचते हैं तो उपयोग किया जाता है|
उदाहरण:
• ग्राहक को सीमेंट की बिक्री

📦 खरीद
  जब आप आइटम खरीदते हैं तो उपयोग किया जाता है|
उदाहरण:
• किराना स्टॉक की खरीद

📘 जर्नल
  नकद या बैंक से संबंधित न होने वाले एडजस्टमेंट और एंट्री के लिए उपयोग किया जाता है।

उदाहरण:
• प्रारंभिक शेष
• खातों के बीच स्थानांतरण/ट्रांसफर 

🔹 भाग 8: मैनेजर

आप अपने व्यवसाय को संभालने के लिए मैनेजर बना सकते हैं। आप उन्हें विशिष्ट शाखा (Branch) सौंप सकते हैं और उनके अधिकार (Rights) तय कर सकते हैं कि वे सॉफ्टवेयर में क्या उपयोग कर सकते हैं।

उदाहरण:
1. एक मैनेजर "A" बनाएं।
2. शाखा सौंपें: "ABC Company"।
3. अधिकार दें: एंट्री, डे बुक, अकाउंट बुक।

परिणाम:
जब मैनेजर "A" लॉगिन करेगा, तो उसे केवल एंट्री, डे बुक और अकाउंट बुक वाले हिस्से ही दिखाई देंगे। बाकी फीचर्स छिपे रहेंगे।

👉 सेटिंग्स → मैनेज यूजर्स / मैनेजर → मैनेजर बनाएं पर जाएं
`
  },
  //   panjabi: {
  //     title: "ਪੰਜਾਬੀ",
  //     description: "ਸੰਪੂਰਨ ਲੇਖਾ ਮਾਰਗਦਰਸ਼ਿਕਾ",
  //     content: \`
  // ਇਸ ਜਾਣਕਾਰੀ ਨੂੰ ਪੜ੍ਹ ਕੇ ਤੁਸੀਂ ਇਸ ਸੌਫਟਵੇਅਰ ਨੂੰ ਆਸਾਨੀ ਨਾਲ ਸਮਝ ਅਤੇ ਵਰਤ ਸਕਦੇ ਹੋ।

  // ਭਾਗ 1: ਖਾਤਾ ਕੀ ਹੈ?

  // ਖਾਤਾ ਉਹ ਜਗ੍ਹਾ ਹੈ ਜਿੱਥੇ ਤੁਸੀਂ ਆਉਣ ਵਾਲੇ ਜਾਂ ਜਾਣ ਵਾਲੇ ਪੈਸੇ ਨੂੰ ਰਿਕਾਰਡ ਕਰਦੇ ਹੋ।

  // ਕਿਸਮ / ਅਰਥ
  // ਸੰਪਤੀ / ਜੋ ਤੁਹਾਡੇ ਕੋਲ ਹੈ
  // ਦੇਣਦਾਰੀਆਂ / ਜੋ ਤੁਸੀਂ ਕਿਸੇ ਨੂੰ ਦੇਣਾ ਹੈ
  // ਆਮਦਨ / ਜੋ ਤੁਸੀਂ ਕਮਾਉਂਦੇ ਹੋ
  // ਖਰਚੇ / ਜੋ ਤੁਸੀਂ ਖਰਚ ਕਰਦੇ ਹੋ

  // ਭਾਗ 2: ਖਾਤਾ ਸੰਰਚਨਾ (ਕਦਮ ਦਰ ਕਦਮ)

  // ਖਾਤਾ ਕਿਸਮ
  //    └── ਗਰੁੱਪ
  //         └── ਉਪ-ਗਰੁੱਪ
  //               └── ਖਾਤਾ ਬਹੀ (ਅਸਲ ਖਾਤਾ)

  // ਭਾਗ 3: ਕਿਹੜੀ ਖਾਤਾ ਬਹੀ ਕਿਸ ਕਿਸਮ ਦੇ ਅਧੀਨ ਆਉਣੀ ਚਾਹੀਦੀ ਹੈ?

  // 🟢 ਸੰਪਤੀ (ਤੁਹਾਡੀ ਸੰਪਤੀ)

  // 1️⃣ ਚਾਲੂ ਸੰਪਤੀ

  // 🔹 ਨਕਦ ਅਤੇ ਬੈਂਕ
  // ਇਸ ਦੇ ਅਧੀਨ ਤੁਸੀਂ ਜੋ ਖਾਤਾ ਬਹੀ ਬਣਾ ਸਕਦੇ ਹੋ:
  // • ਹੱਥ ਵਿੱਚ ਨਕਦ
  // • ਐਸਬੀਆਈ ਬੈਂਕ
  // • ਚਾਲੂ ਖਾਤਾ
  // • ਬੱਚਤ ਖਾਤਾ
  // • ਯੂਪੀਆਈ ਖਾਤਾ

  // 🔹 ਦੇਣਦਾਰ (ਗਾਹਕ)
  // ਇਸ ਦੇ ਅਧੀਨ ਤੁਸੀਂ ਜੋ ਖਾਤਾ ਬਹੀ ਬਣਾ ਸਕਦੇ ਹੋ:
  // • ਗਾਹਕ – ਰਾਮ ਟ੍ਰੇਡਰਜ਼

  // ✔ ਕੋਈ ਵੀ ਜੋ ਤੁਹਾਡੇ ਤੋਂ ਖਰੀਦਦਾ ਹੈ ਅਤੇ ਬਾਅਦ ਵਿੱਚ ਭੁਗਤਾਨ ਕਰਦਾ ਹੈ

  // 🔹 ਸਟਾਕ / ਇਨਵੈਂਟਰੀ
  // ਇਸ ਦੇ ਅਧੀਨ ਤੁਸੀਂ ਜੋ ਖਾਤਾ ਬਹੀ ਬਣਾ ਸਕਦੇ ਹੋ:
  // • ਕਲੋਜ਼ਿੰਗ ਸਟਾਕ
  // • ਓਪਨਿੰਗ ਸਟਾਕ

  // 2️⃣ ਸਥਾਈ ਸੰਪਤੀਆਂ
  // ਇਸ ਦੇ ਅਧੀਨ ਤੁਸੀਂ ਜੋ ਖਾਤਾ ਬਹੀ ਬਣਾ ਸਕਦੇ ਹੋ:
  // • ਇਮਾਰਤ
  // • ਮਸ਼ੀਨਰੀ
  // • ਡਿਲਿਵਰੀ ਵਾਹਨ

  // 🔴 ਦੇਣਦਾਰੀਆਂ (ਤੁਹਾਡੇ ਕਰਜ਼ੇ)

  // 1️⃣ ਪੂੰਜੀ ਖਾਤਾ
  // ਇਸ ਦੇ ਅਧੀਨ ਤੁਸੀਂ ਜੋ ਖਾਤਾ ਬਹੀ ਬਣਾ ਸਕਦੇ ਹੋ:
  // • ਮਾਲਕ ਪੂੰਜੀ
  // • ਭਾਈਵਾਲ ਪੂੰਜੀ

  // 2️⃣ ਚਾਲੂ ਦੇਣਦਾਰੀਆਂ

  // 🔹 ਲੈਣਦਾਰ (ਸਪਲਾਇਰ)
  // ਇਸ ਦੇ ਅਧੀਨ ਤੁਸੀਂ ਜੋ ਖਾਤਾ ਬਹੀ ਬਣਾ ਸਕਦੇ ਹੋ:
  // • ਸਪਲਾਇਰ – ਗੁਪਤਾ ਟ੍ਰੇਡਰਜ਼
  // • ਸਪਲਾਇਰ – ਸਟੀਲ ਵਰਲਡ

  // ✔ ਕੋਈ ਵੀ ਜਿਸ ਤੋਂ ਤੁਸੀਂ ਮਾਲ ਖਰੀਦਦੇ ਹੋ ਅਤੇ ਬਾਅਦ ਵਿੱਚ ਭੁਗਤਾਨ ਕਰਦੇ ਹੋ

  // 3️⃣ ਕਰਜ਼ਾ
  // ਇਸ ਦੇ ਅਧੀਨ ਤੁਸੀਂ ਜੋ ਖਾਤਾ ਬਹੀ ਬਣਾ ਸਕਦੇ ਹੋ:
  // • ਐਸਬੀਆਈ ਕਰਜ਼ਾ

  // 🔵 ਆਮਦਨ (ਕਮਾਈ)

  // 1️⃣ ਸਿੱਧੀ ਆਮਦਨ
  // ਇਸ ਦੇ ਅਧੀਨ ਤੁਸੀਂ ਜੋ ਖਾਤਾ ਬਹੀ ਬਣਾ ਸਕਦੇ ਹੋ:
  // • ਵਿਕਰੀ ਖਾਤਾ
  // • ਸੀਮੈਂਟ - ਵਿਕਰੀ
  // • ਕਿਰਿਆਨਾ - ਵਿਕਰੀ

  // 2️⃣ ਅਸਿੱਧੀ ਆਮਦਨ
  // ਇਸ ਦੇ ਅਧੀਨ ਤੁਸੀਂ ਜੋ ਖਾਤਾ ਬਹੀ ਬਣਾ ਸਕਦੇ ਹੋ:
  // • ਵਿਆਜ ਪ੍ਰਾਪਤ
  // • ਕਮਿਸ਼ਨ ਪ੍ਰਾਪਤ
  // • ਛੋਟ ਪ੍ਰਾਪਤ
  // • ਕਿਰਾਏ ਦੀ ਆਮਦਨ

  // 🟡 ਖਰਚੇ (ਖਰਚਾ)

  // 1️⃣ ਸਿੱਧੇ ਖਰਚੇ
  // ਇਸ ਦੇ ਅਧੀਨ ਤੁਸੀਂ ਜੋ ਖਾਤਾ ਬਹੀ ਬਣਾ ਸਕਦੇ ਹੋ:
  // • ਖਰੀਦ ਖਾਤਾ
  // • ਸੀਮੈਂਟ - ਖਰੀਦ

  // 2️⃣ ਅਸਿੱਧੇ ਖਰਚੇ

  // 🔹 ਦਫ਼ਤਰ ਅਤੇ ਆਮ ਖਰਚੇ
  // ਇਸ ਦੇ ਅਧੀਨ ਤੁਸੀਂ ਜੋ ਖਾਤਾ ਬਹੀ ਬਣਾ ਸਕਦੇ ਹੋ:
  // • ਕਿਰਾਇਆ ਖਰਚਾ
  // • ਬਿਜਲੀ ਖਰਚਾ

  // 🔹 ਤਨਖਾਹ ਅਤੇ ਮਜ਼ਦੂਰੀ
  // ਇਸ ਦੇ ਅਧੀਨ ਤੁਸੀਂ ਜੋ ਖਾਤਾ ਬਹੀ ਬਣਾ ਸਕਦੇ ਹੋ:
  // • ਤਨਖਾਹ ਖਰਚਾ

  // 🔹 ਟਰਾਂਸਪੋਰਟ ਖਰਚਾ
  // ਇਸ ਦੇ ਅਧੀਨ ਤੁਸੀਂ ਜੋ ਖਾਤਾ ਬਹੀ ਬਣਾ ਸਕਦੇ ਹੋ:
  // • ਟਰਾਂਸਪੋਰਟ ਖਰਚਾ

  // 🔹 ਹੋਰ ਖਰਚੇ
  // ਇਸ ਦੇ ਅਧੀਨ ਤੁਸੀਂ ਜੋ ਖਾਤਾ ਬਹੀ ਬਣਾ ਸਕਦੇ ਹੋ:
  // • ਮੁਰੰਮਤ ਅਤੇ ਰੱਖ-ਰਖਾਅ
  // • ਇਸ਼ਤਿਹਾਰਬਾਜ਼ੀ ਖਰਚਾ
  // • ਕਮਿਸ਼ਨ ਭੁਗਤਾਨ
  // • ਛੋਟ

  // ਭਾਗ 4: ਇਨਵੈਂਟਰੀ (ਆਈਟਮ) ਕਿਵੇਂ ਬਣਾਈਏ?

  // ਕਦਮ 1: ਸ਼੍ਰੇਣੀਆਂ ਬਣਾਓ
  // • ਸੀਮੈਂਟ
  // • ਕਿਰਿਆਨਾ
  // • ਇਲੈਕਟ੍ਰੋਨਿਕਸ

  // ਕਦਮ 2: ਸ਼੍ਰੇਣੀਆਂ ਦੇ ਅਧੀਨ ਆਈਟਮਾਂ ਜੋੜੋ
  // • ਸੀਮੈਂਟ → ਅਲਟ੍ਰਾਟੈਕ ਸੀਮੈਂਟ
  // • ਕਿਰਿਆਨਾ → ਚੀਨੀ, ਚਾਵਲ

  // 🔹 ਭਾਗ 5: ਭੁਗਤਾਨ ਕਿਸਮ ਸੈਟਅੱਪ

  // Q. ਭੁਗਤਾਨ ਕਿਸਮ ਕੀ ਹੈ?
  // ਭੁਗਤਾਨ ਕਿਸਮ ਦਾ ਮਤਲਬ ਹੈ ਕਿ ਤੁਸੀਂ ਪੈਸੇ ਕਿਵੇਂ ਪ੍ਰਾਪਤ ਕਰਦੇ ਜਾਂ ਦਿੰਦੇ ਹੋ।

  // ਉਦਾਹਰਨਾਂ:
  // • ਨਕਦ
  // • ਐਸਬੀਆਈ ਬੈਂਕ
  // • ਯੂਪੀਆਈ

  // Q. ਭੁਗਤਾਨ ਕਿਸਮ ਕਿਵੇਂ ਬਣਾਈਏ?
  // 1. ਸੈਟਿੰਗਜ਼ → ਭੁਗਤਾਨ ਕਿਸਮ ਵਿੱਚ ਜਾਓ
  // 2. ਭੁਗਤਾਨ ਕਿਸਮ ਬਣਾਓ
  // 3. ਇਸਨੂੰ ਇੱਕ ਖਾਤਾ ਬਹੀ ਨਾਲ ਜੋੜੋ

  // ਉਦਾਹਰਨ ਮੈਪਿੰਗ:
  // ਭੁਗਤਾਨ ਕਿਸਮ / ਜੁੜੀ ਖਾਤਾ ਬਹੀ
  // ਨਕਦ / ਹੱਥ ਵਿੱਚ ਨਕਦ
  // ਐਸਬੀਆਈ / ਐਸਬੀਆਈ ਬੈਂਕ
  // ਯੂਪੀਆਈ / ਐਚਡੀਐਫਸੀ ਬੈਂਕ

  // ✔ ਜੇ ਤੁਹਾਡੇ ਕੋਲ 2 ਬੈਂਕ ਖਾਤੇ ਹਨ, ਤਾਂ 2 ਭੁਗਤਾਨ ਕਿਸਮਾਂ ਬਣਾਓ।
  //✔  ਜੇ ਤੁਸੀਂ ਐਂਟਰੀ ਦੇ ਸਮੇਂ ਕਿਸੇ ਭੁਗਤਾਨ ਕਿਸਮ ਨੂੰ ਡਿਫ਼ਾਲਟ ਰੱਖਣਾ ਚਾਹੁੰਦੇ ਹੋ, ਤਾਂ ਤੁਹਾਨੂੰ ਉਸਨੂੰ ਪੇਮੈਂਟ ਟੇਬਲ ਵਿੱਚ ਚੁਣਨਾ ਹੋਵੇਗਾ। ਚੁਣੀ ਗਈ ਭੁਗਤਾਨ ਕਿਸਮ ਤੁਹਾਡੀ ਐਂਟਰੀ ਵਿੱਚ ਆਪਣੇ ਆਪ ਡਿਫ਼ਾਲਟ ਵਜੋਂ ਲਾਗੂ ਹੋ ਜਾਵੇਗੀ।

  // 🔹 ਭਾਗ 5.5: ਵਿਕਰੀ ਅਤੇ ਖਰੀਦ ਵਿੱਚ ਟਰਾਂਸਪੋਰਟ ਅਤੇ ਲੋਡਿੰਗ ਦਿਖਾਓ

  // 👉 ਸੈਟਿੰਗਜ਼ → ਵਿਕਰੀ ਅਤੇ ਖਰੀਦ ਵਿੱਚ ਟਰਾਂਸਪੋਰਟ ਅਤੇ ਲੋਡਿੰਗ ਦਿਖਾਓ ਵਿੱਚ ਜਾਓ

  // ਵਿਕਲਪ / ਇਹ ਕੀ ਕਰਦਾ ਹੈ
  // 🚚 ਸਿਰਫ਼ ਟਰਾਂਸਪੋਰਟ / ਵਿਕਰੀ ਅਤੇ ਖਰੀਦ ਐਂਟਰੀ ਵਿੱਚ ਟਰਾਂਸਪੋਰਟ ਚਾਰਜ ਖਾਨਾ ਦਿਖਾਉਂਦਾ ਹੈ
  // 🏗️ ਸਿਰਫ਼ ਲੋਡਿੰਗ / ਵਿਕਰੀ ਅਤੇ ਖਰੀਦ ਐਂਟਰੀ ਵਿੱਚ ਲੋਡਿੰਗ ਚਾਰਜ ਖਾਨਾ ਦਿਖਾਉਂਦਾ ਹੈ
  // ✅ ਦੋਵੇਂ / ਟਰਾਂਸਪੋਰਟ ਅਤੇ ਲੋਡਿੰਗ ਦੋਵੇਂ ਖਾਨੇ ਦਿਖਾਉਂਦਾ ਹੈ
  // ❌ ਕੋਈ ਨਹੀਂ / ਦੋਵੇਂ ਖਾਨੇ ਲੁਕਾਉਂਦਾ ਹੈ (ਡਿਫੌਲਟ)

  // ✔ ਇੱਕ ਵਾਰ ਚਾਲੂ ਹੋਣ ਤੋਂ ਬਾਅਦ, ਟਰਾਂਸਪੋਰਟ ਅਤੇ ਲੋਡਿੰਗ ਖਾਨੇ ਹਰ ਵਿਕਰੀ ਅਤੇ ਖਰੀਦ ਐਂਟਰੀ ਵਿੱਚ ਆਪਣੇ ਆਪ ਦਿਖਣਗੇ।

  // ਭਾਗ 6: ਟਰਾਂਸਪੋਰਟ ਖੇਤਰ ਅਤੇ ਭਾੜਾ

  // ਕਦਮ 1: ਟਰਾਂਸਪੋਰਟ ਖੇਤਰ ਬਣਾਓ
  // • ਸ਼ਹਿਰ ਏ
  // • ਸ਼ਹਿਰ ਬੀ

  // ਕਦਮ 2: ਹਰ ਖੇਤਰ ਲਈ ਭਾੜਾ ਜੋੜੋ
  // • ਸ਼ਹਿਰ ਏ → ₹500
  // • ਸ਼ਹਿਰ ਬੀ → ₹800

  // Q. ਇਹ ਕਿਵੇਂ ਕੰਮ ਕਰਦਾ ਹੈ?
  // ✔ ਟਰਾਂਸਪੋਰਟ ਭਾੜਾ ਆਪਣੇ ਆਪ ਜੋੜਿਆ ਜਾਂਦਾ ਹੈ
  // ✔ ਰਕਮ ਟਰਾਂਸਪੋਰਟ ਖਰਚਾ ਖਾਤਾ ਬਹੀ ਵਿੱਚ ਜਾਂਦੀ ਹੈ

  // 🔹 ਭਾਗ 6.5: ਐਂਟਰੀ ਫਿਲਟਰ ਸੈਟਅੱਪ (ਵਾਊਚਰ ਇਨਪੁੱਟ ਵਿੱਚ ਸਰਚ ਫਿਲਟਰ)

  // ਜਦੋਂ ਤੁਸੀਂ ਵਿਕਰੀ, ਖਰੀਦ, ਰਸੀਦ, ਭੁਗਤਾਨ ਜਾਂ ਜਰਨਲ ਐਂਟਰੀ ਬਣਾਉਂਦੇ ਹੋ, ਤਾਂ ਹਰ ਇਨਪੁੱਟ ਫੀਲਡ ਵਿੱਚ ਇੱਕ ਸਰਚ ਫਿਲਟਰ ਦਾ ਵਿਕਲਪ ਹੁੰਦਾ ਹੈ। ਇਸਦੀ ਵਰਤੋਂ ਕਰਕੇ ਤੁਸੀਂ ਕੰਟਰੋਲ ਕਰ ਸਕਦੇ ਹੋ ਕਿ ਉਸ ਇਨਪੁੱਟ ਵਿੱਚ ਕਿਹੜੇ ਗਰੁੱਪ ਅਤੇ ਸਬ-ਗਰੁੱਪ ਸੁਝਾਅ ਵਜੋਂ ਦਿਖਣ।

  // 👉 ਕੋਈ ਵੀ ਵਾਊਚਰ ਐਂਟਰੀ ਖੋਲ੍ਹੋ → ਕਿਸੇ ਵੀ ਇਨਪੁੱਟ ਫੀਲਡ 'ਤੇ ਸਰਚ ਫਿਲਟਰ ਆਈਕਨ ਕਲਿੱਕ ਕਰੋ → ਜੋ ਗਰੁੱਪ ਜਾਂ ਸਬ-ਗਰੁੱਪ ਦਿਖਾਉਣਾ ਹੋਵੇ ਉਹ ਚੁਣੋ

  // ਇਹ ਕਿਉਂ ਲਾਭਦਾਇਕ ਹੈ:
  // ✔ ਵਿਕਰੀ ਐਂਟਰੀ ਵਿੱਚ → ਸਿਰਫ਼ ਦੇਣਦਾਰ (ਗਾਹਕ) ਦਿਖਾਓ
  // ✔ ਖਰੀਦ ਐਂਟਰੀ ਵਿੱਚ → ਸਿਰਫ਼ ਲੈਣਦਾਰ (ਸਪਲਾਇਰ) ਦਿਖਾਓ
  // ✔ ਰਸੀਦ ਐਂਟਰੀ ਵਿੱਚ → ਦੇਣਦਾਰ + ਨਕਦ ਅਤੇ ਬੈਂਕ ਦਿਖਾਓ
  // ✔ ਭੁਗਤਾਨ ਐਂਟਰੀ ਵਿੱਚ → ਲੈਣਦਾਰ + ਨਕਦ ਅਤੇ ਬੈਂਕ ਦਿਖਾਓ
  // ✔ ਜਰਨਲ ਐਂਟਰੀ ਵਿੱਚ → ਲੋੜ ਅਨੁਸਾਰ ਸਾਰੇ ਗਰੁੱਪ ਦਿਖਾਓ

  // ਵਾਊਚਰ ਕਿਸਮ / ਸੁਝਾਅ ਦਿੱਤਾ ਫਿਲਟਰ
  // 🛒 ਵਿਕਰੀ / ਦੇਣਦਾਰ (ਗਾਹਕ)
  // 📦 ਖਰੀਦ / ਲੈਣਦਾਰ (ਸਪਲਾਇਰ)
  // 📑 ਰਸੀਦ / ਦੇਣਦਾਰ + ਨਕਦ ਅਤੇ ਬੈਂਕ
  // 💸 ਭੁਗਤਾਨ / ਲੈਣਦਾਰ + ਨਕਦ ਅਤੇ ਬੈਂਕ
  // 📘 ਜਰਨਲ / ਲੋੜ ਅਨੁਸਾਰ ਸਾਰੇ ਗਰੁੱਪ

  // ✔ ਇਸ ਨਾਲ ਸਹੀ ਖਾਤਾ ਬਹੀ ਜਲਦੀ ਮਿਲਦੀ ਹੈ — ਸਾਰੇ ਖਾਤਿਆਂ ਵਿੱਚ ਸਕ੍ਰੋਲ ਨਹੀਂ ਕਰਨਾ ਪੈਂਦਾ!

  // ਭਾਗ 7: ਵਾਊਚਰ ਐਂਟਰੀ (ਰੋਜ਼ਾਨਾ ਕੰਮ)

  // 📑 ਰਸੀਦ
  // ਜਦੋਂ ਤੁਸੀਂ ਪੈਸੇ ਪ੍ਰਾਪਤ ਕਰਦੇ ਹੋ
  // ਉਦਾਹਰਨ: ਗਾਹਕ ਨਕਦ ਭੁਗਤਾਨ ਕਰਦਾ ਹੈ

  // 💸 ਭੁਗਤਾਨ
  // ਜਦੋਂ ਤੁਸੀਂ ਪੈਸੇ ਦਿੰਦੇ ਹੋ
  // ਉਦਾਹਰਨ:
  // • ਕਿਰਾਇਆ ਭੁਗਤਾਨ
  // • ਬਿਜਲੀ ਬਿੱਲ ਭੁਗਤਾਨ

  // 🛒 ਵਿਕਰੀ
  // ਜਦੋਂ ਤੁਸੀਂ ਆਈਟਮਾਂ ਵੇਚਦੇ ਹੋ
  // ਉਦਾਹਰਨ: ਗਾਹਕ ਨੂੰ ਸੀਮੈਂਟ ਦੀ ਵਿਕਰੀ

  // 📦 ਖਰੀਦ
  // ਜਦੋਂ ਤੁਸੀਂ ਆਈਟਮਾਂ ਖਰੀਦਦੇ ਹੋ
  // ਉਦਾਹਰਨ: ਕਿਰਿਆਨਾ ਸਟਾਕ ਦੀ ਖਰੀਦ

  // 📘 ਜਰਨਲ
  // ਨਕਦ ਜਾਂ ਬੈਂਕ ਤੋਂ ਬਿਨਾਂ ਐਡਜਸਟਮੈਂਟ ਅਤੇ ਐਂਟਰੀਆਂ ਲਈ।
  // ਉਦਾਹਰਨ:
  // • ਸ਼ੁਰੂਆਤੀ ਬਕਾਇਆ
  // • ਖਾਤਿਆਂ ਵਿਚਕਾਰ ਟ੍ਰਾਂਸਫਰ
  // \`
  //   }
};


const setupStepsData = {
  assets: [
    {
      icon: "🌐", title: "Language Setup", subtitle: "Step 1",
      desc: "Configure software language & second language translations",
      details: ["👤 User Profile → Change Software Language", "⚙️ Settings → Second Language Translation"],
      color: "#3b82f6", bgColor: "#eff6ff", borderColor: "#bfdbfe"
    },
    {
      icon: "⚙️", title: "Transportation & Loading in Sales/Purchase", subtitle: "Step 2",
      desc: "Enable transport & loading expense directly in sales & purchase entries",
      details: [
        "⚙️ Settings → Show Transportation & Loading in Sales & Purchase",
        "✅ Select your option from below table",
      ],

      note: "💡 Once enabled, fields appear automatically in every entry!",
      color: "#0ea5e9", bgColor: "#f0f9ff", borderColor: "#bae6fd"
    },
    {
      icon: "🚚", title: "Transportation Area & Fare", subtitle: "Step 3",
      desc: "Set delivery areas & fares to auto-add to transport expenses",
      details: ["⚙️ Settings → Transportation Area & Fare", "📍 Add Areas: City A, City B", "💰 Set Fares: ₹500, ₹800"],
      color: "#f97316", bgColor: "#fff7ed", borderColor: "#fed7aa"
    },
    {
      icon: "📚", title: "Groups, Sub-groups & Ledgers", subtitle: "Step 4",
      desc: "Create account structure under 4 main types",
      details: ["📖 Account Book → Create", "💰 Add Opening Balance", "📝 Examples: Cash, Bank, Sales, Purchase"],
      color: "#22c55e", bgColor: "#f0fdf4", borderColor: "#bbf7d0"
    },
    {
      icon: "📦", title: "Categories & Items", subtitle: "Step 5",
      desc: "Create inventory for sales and purchases",
      details: ["📦 Inventory Book → Create", "📂 Categories: bricks, raw material", "🏷️ Items: UltraTech 1 nbr, koyla"],
      color: "#a855f7", bgColor: "#faf5ff", borderColor: "#e9d5ff"
    },
    {
      icon: "💳", title: "Payment Types", subtitle: "Step 6",
      desc: "Connect payment methods to ledgers",
      details: ["⚙️ Settings → Payment Types", "💵 Cash → Cash-in-Hand", "🏦 Bank → SBI Bank, HDFC Bank", "✔ If you want to set a payment type as default during entry, you need to select it in the Payment Table. The selected payment type will automatically appear as the default in your entries."],
      color: "#ec4899", bgColor: "#fdf2f8", borderColor: "#fbcfe8"
    },
    {
      icon: "🔍", title: "Entry Filter Setup", subtitle: "Step 7",
      desc: "Filter which Groups & Sub-groups appear in each voucher entry input",
      details: [
        "📋 Open any Voucher: Sale / Purchase / Receipt / Payment / Journal",
        "🔍 Click Search Filter on any input field",
        "✅ Select which Group or Sub-group values to show",
      ],

      note: "💡 This helps you find the right ledger quickly without scrolling through all accounts!",
      color: "#f59e0b", bgColor: "#fffbeb", borderColor: "#fde68a"
    },
    {
      icon: "📒", title: "Journal Item Settings", subtitle: "Step 8",
      desc: "Enable Items, Stock & Quantity columns in Journal Voucher",
      details: [
        "⚙️ Settings → More Options",
        "✅ Enable: Show Items, Manage Stock & Quantity in Journal Entry"
      ],
      note: "💡 Useful for adjustments involving inventory items!",
      color: "#8b5cf6", bgColor: "#f5f3ff", borderColor: "#ddd6fe"
    },
    {
      icon: "📝", title: "Register Entry", subtitle: "Step 9",
      desc: "Start recording daily transactions",
      details: ["🧾 Voucher Entries", "💰 Receipt, Payment, Sale, Purchase", "🎉 Start Using Software!"],
      color: "#6366f1", bgColor: "#eef2ff", borderColor: "#c7d2fe", isLast: true
    },
    {
      icon: "👨‍💼", title: "Manager", subtitle: "Step 10",
      desc: "Create managers with specific branches & rights",
      details: ["⚙️ Settings → Manage Users", "👤 Create Manager: Name, Password", "🏢 Assign Branch: ABC Company", "🔒 Define Rights: Entry, Day Book"],
      color: "#ec4899", bgColor: "#fdf2f8", borderColor: "#fbcfe8"
    },
  ],
  hindi: [
    {
      icon: "🌐", title: "भाषा सेटअप", subtitle: "Step 1",
      desc: "सॉफ्टवेयर की भाषा और दूसरी भाषा का अनुवाद सेटअप करें",
      details: ["👤 यूजर प्रोफाइल → भाषा → भाषा बदलें", "⚙️ सेटिंग्स → दूसरी भाषा अनुवाद → भाषा → भाषा बदलें"],
      color: "#3b82f6", bgColor: "#eff6ff", borderColor: "#bfdbfe"
    },
    {
      icon: "⚙️", title: "बिक्री/खरीद में परिवहन और लोडिंग", subtitle: "Step 2",
      desc: "बिक्री और खरीद में परिवहन और लोडिंग खर्च सीधे जोड़ने की सुविधा चालू करें",
      details: [
        "⚙️ सेटिंग्स → बिक्री और खरीद में परिवहन और लोडिंग दिखाएं",
        "✅ नीचे दी गई तालिका से विकल्प चुनें",
      ],
      note: "💡 एक बार चालू होने पर हर एंट्री में अपने आप दिखेगा!",
      color: "#0ea5e9", bgColor: "#f0f9ff", borderColor: "#bae6fd"
    },
    {
      icon: "🚚", title: "परिवहन क्षेत्र और किराया", subtitle: "Step 3",
      desc: "डिलीवरी क्षेत्र और किराया सेट करें जो खर्च में जोड़ा जाएगा",
      details: ["⚙️ सेटिंग्स → परिवहन क्षेत्र और किराया", "📍 क्षेत्र जोड़ें: शहर A, शहर B", "💰 किराया निर्धारित करें: ₹500, ₹800"],
      color: "#f97316", bgColor: "#fff7ed", borderColor: "#fed7aa"
    },
    {
      icon: "📚", title: "ग्रुप, उप-ग्रुप और बही खाता", subtitle: "Step 4",
      desc: "4 मुख्य प्रकारों में खाता संरचना बनाएं",
      details: ["📖 अकाउंट बुक → क्रिएट", "💰 ओपनिंग बैलेंस जोड़ें", "📝 उदाहरण: नकद, बैंक, बिक्री, खरीद"],
      color: "#22c55e", bgColor: "#f0fdf4", borderColor: "#bbf7d0"
    },
    {
      icon: "📦", title: "श्रेणी और आइटम", subtitle: "Step 5",
      desc: "बिक्री और खरीद के लिए इन्वेंटरी बनाएं",
      details: ["📦 इन्वेंटरी बुक → क्रिएट", "📂 श्रेणियां: ब्रिक्स", "🏷️ आइटम: 1 nbr, कोयला"],
      color: "#a855f7", bgColor: "#faf5ff", borderColor: "#e9d5ff"
    },
    {
      icon: "💳", title: "भुगतान प्रकार", subtitle: "Step 6",
      desc: "भुगतान विधियों को बही खातों से कनेक्ट करें",
      details: ["⚙️ सेटिंग्स → भुगतान प्रकार", "💵 नकद → हाथ में नकद", "🏦 बैंक → एसबीआई बैंक, एचडीएफसी बैंक", "✔ यदि आप एंट्री के समय किसी भुगतान प्रकार को डिफ़ॉल्ट रखना चाहते हैं, तो आपको उसे पेमेंट टेबल में चुनना होगा। चयनित भुगतान प्रकार आपकी एंट्री में स्वतः डिफ़ॉल्ट रूप से लागू हो जाएगा।"],
      color: "#ec4899", bgColor: "#fdf2f8", borderColor: "#fbcfe8"
    },
    {
      icon: "🔍", title: "एंट्री फ़िल्टर सेटअप", subtitle: "Step 7",
      desc: "प्रत्येक वाउचर एंट्री इनपुट में कौन से ग्रुप और सब-ग्रुप दिखें, यह फ़िल्टर करें",
      details: [
        "📋 कोई भी वाउचर खोलें: बिक्री / खरीद / रसीद / भुगतान / जर्नल",
        "🔍 किसी भी इनपुट फील्ड पर सर्च फ़िल्टर पर क्लिक करें",
        "✅ चुनें कि कौन से ग्रुप या सब-ग्रुप की वैल्यू दिखानी हैं",
      ],
      note: "💡 इससे सही बही खाता जल्दी मिलता है — सभी खातों में स्क्रॉल नहीं करना पड़ता!",
      color: "#f59e0b", bgColor: "#fffbeb", borderColor: "#fde68a"
    },
    {
      icon: "📒", title: "जर्नल आइटम सेटिंग्स", subtitle: "Step 8",
      desc: "जर्नल वाउचर में आइटम, स्टॉक और मात्रा कॉलम सक्षम करें",
      details: [
        "⚙️ सेटिंग्स → अधिक विकल्प (More Options)",
        "✅ सक्षम करें: जर्नल एंट्री में आइटम, स्टॉक और मात्रा दिखाएं"
      ],
      note: "💡 इन्वेंट्री आइटम वाले समायोजन के लिए उपयोगी!",
      color: "#8b5cf6", bgColor: "#f5f3ff", borderColor: "#ddd6fe"
    },
    {
      icon: "📝", title: "रजिस्टर एंट्री", subtitle: "Step 9",
      desc: "रोजमर्रा के लेन-देन रिकॉर्ड करना शुरू करें",
      details: ["🧾 वाउचर एंट्री", "💰 रसीद, भुगतान, बिक्री, खरीद", "🎉 सॉफ्टवेयर का उपयोग शुरू करें!"],
      color: "#6366f1", bgColor: "#eef2ff", borderColor: "#c7d2fe", isLast: true
    },
    {
      icon: "👨‍💼", title: "मैनेजर", subtitle: "Step 10",
      desc: "मैनेजर बनाएं और विशिष्ट शाखा और अनुमति दें",
      details: ["⚙️ सेटिंग्स → मैनेज यूजर्स", "👤 मैनेजर बनाएं: नाम, पासवर्ड", "🏢 शाखा सौंपें: ABC Company", "🔒 अधिकार तय करें: एंट्री, डे बुक"],
      color: "#ec4899", bgColor: "#fdf2f8", borderColor: "#fbcfe8"
    },
  ],
  panjabi: [
    {
      icon: "🌐", title: "ਭਾਸ਼ਾ ਸੈਟਅੱਪ", subtitle: "ਕਦਮ 1",
      desc: "ਸਾਫਟਵੇਅਰ ਭਾਸ਼ਾ ਅਤੇ ਦੂਜੀ ਭਾਸ਼ਾ ਅਨੁਵਾਦ ਸੈਟਅੱਪ ਕਰੋ",
      details: ["👤 ਯੂਜ਼ਰ ਪ੍ਰੋਫਾਈਲ → ਸਾਫਟਵੇਅਰ ਭਾਸ਼ਾ ਬਦਲੋ", "⚙️ ਸੈਟਿੰਗਜ਼ → ਦੂਜੀ ਭਾਸ਼ਾ ਅਨੁਵਾਦ"],
      color: "#3b82f6", bgColor: "#eff6ff", borderColor: "#bfdbfe"
    },
    {
      icon: "⚙️", title: "ਵਿਕਰੀ/ਖਰੀਦ ਵਿੱਚ ਟਰਾਂਸਪੋਰਟ ਅਤੇ ਲੋਡਿੰਗ", subtitle: "ਕਦਮ 2",
      desc: "ਵਿਕਰੀ ਅਤੇ ਖਰੀਦ ਵਿੱਚ ਟਰਾਂਸਪੋਰਟ ਅਤੇ ਲੋਡਿੰਗ ਖਰਚਾ ਸਿੱਧਾ ਜੋੜਨ ਦੀ ਸਹੂਲਤ ਚਾਲੂ ਕਰੋ",
      details: [
        "⚙️ ਸੈਟਿੰਗਜ਼ → ਵਿਕਰੀ ਅਤੇ ਖਰੀਦ ਵਿੱਚ ਟਰਾਂਸਪੋਰਟ ਅਤੇ ਲੋਡਿੰਗ ਦਿਖਾਓ",
        "✅ ਹੇਠਾਂ ਦਿੱਤੀ ਸਾਰਣੀ ਤੋਂ ਵਿਕਲਪ ਚੁਣੋ",
      ],

      note: "💡 ਇੱਕ ਵਾਰ ਚਾਲੂ ਹੋਣ ਤੋਂ ਬਾਅਦ ਹਰ ਐਂਟਰੀ ਵਿੱਚ ਆਪਣੇ ਆਪ ਦਿਖੇਗਾ!",
      color: "#0ea5e9", bgColor: "#f0f9ff", borderColor: "#bae6fd"
    },
    {
      icon: "🚚", title: "ਟਰਾਂਸਪੋਰਟ ਖੇਤਰ ਅਤੇ ਭਾੜਾ", subtitle: "ਕਦਮ 3",
      desc: "ਡਿਲਿਵਰੀ ਖੇਤਰ ਅਤੇ ਭਾੜਾ ਸੈਟ ਕਰੋ ਜੋ ਖਰਚ ਵਿੱਚ ਜੋੜਿਆ ਜਾਵੇਗਾ",
      details: ["⚙️ ਸੈਟਿੰਗਜ਼ → ਟਰਾਂਸਪੋਰਟ ਖੇਤਰ ਅਤੇ ਭਾੜਾ", "📍 ਖੇਤਰ ਸ਼ਾਮਲ ਕਰੋ: ਸ਼ਹਿਰ A, ਸ਼ਹਿਰ B", "💰 ਭਾੜਾ ਨਿਰਧਾਰਿਤ ਕਰੋ: ₹500, ₹800"],
      color: "#f97316", bgColor: "#fff7ed", borderColor: "#fed7aa"
    },
    {
      icon: "📚", title: "ਗਰੁੱਪ, ਉਪ-ਗਰੁੱਪ ਅਤੇ ਖਾਤਾ ਬਹੀ", subtitle: "ਕਦਮ 4",
      desc: "4 ਮੁੱਖ ਕਿਸਮਾਂ ਵਿੱਚ ਖਾਤਾ ਸੰਰਚਨਾ ਬਣਾਓ",
      details: ["📖 ਅਕਾਊਂਟ ਬੁੱਕ → ਬਣਾਓ", "💰 ਓਪਨਿੰਗ ਬਕਾਇਆ ਸ਼ਾਮਲ ਕਰੋ", "📝 ਉਦਾਹਰਨ: ਨਕਦ, ਬੈਂਕ, ਵਿਕਰੀ, ਖਰੀਦ"],
      color: "#22c55e", bgColor: "#f0fdf4", borderColor: "#bbf7d0"
    },
    {
      icon: "📦", title: "ਸ਼੍ਰੇਣੀ ਅਤੇ ਆਈਟਮ", subtitle: "ਕਦਮ 5",
      desc: "ਵਿਕਰੀ ਅਤੇ ਖਰੀਦ ਲਈ ਇਨਵੈਂਟਰੀ ਬਣਾਓ",
      details: ["📦 ਇਨਵੈਂਟਰੀ ਬੁੱਕ → ਬਣਾਓ", "📂 ਸ਼੍ਰੇਣੀਆਂ: ਸੀਮੈਂਟ, ਕਿਰਿਆਨਾ", "🏷️ ਆਈਟਮ: ਅਲਟ੍ਰਾਟੈਕ ਸੀਮੈਂਟ, ਚੀਨੀ"],
      color: "#a855f7", bgColor: "#faf5ff", borderColor: "#e9d5ff"
    },
    {
      icon: "💳", title: "ਭੁਗਤਾਨ ਕਿਸਮ", subtitle: "ਕਦਮ 6",
      desc: "ਭੁਗਤਾਨ ਵਿਧੀਆਂ ਨੂੰ ਖਾਤਾ ਬਹੀ ਨਾਲ ਜੋੜੋ",
      details: ["⚙️ ਸੈਟਿੰਗਜ਼ → ਭੁਗਤਾਨ ਕਿਸਮ", "💵 ਨਕਦ → ਹੱਥ ਵਿੱਚ ਨਕਦ", "🏦 ਬੈਂਕ → ਐਸਬੀਆਈ ਬੈਂਕ, ਐਚਡੀਐਫਸੀ ਬੈਂਕ", "✔ ਜੇ ਤੁਸੀਂ ਐਂਟਰੀ ਦੇ ਸਮੇਂ ਕਿਸੇ ਭੁਗਤਾਨ ਕਿਸਮ ਨੂੰ ਡਿਫ਼ਾਲਟ ਰੱਖਣਾ ਚਾਹੁੰਦੇ ਹੋ, ਤਾਂ ਤੁਹਾਨੂੰ ਉਸਨੂੰ ਪੇਮੈਂਟ ਟੇਬਲ ਵਿੱਚ ਚੁਣਨਾ ਹੋਵੇਗਾ। ਚੁਣੀ ਗਈ ਭੁਗਤਾਨ ਕਿਸਮ ਤੁਹਾਡੀ ਐਂਟਰੀ ਵਿੱਚ ਆਪਣੇ ਆਪ ਡਿਫ਼ਾਲਟ ਵਜੋਂ ਲਾਗੂ ਹੋ ਜਾਵੇਗੀ।"],
      color: "#ec4899", bgColor: "#fdf2f8", borderColor: "#fbcfe8"
    },
    {
      icon: "🔍", title: "ਐਂਟਰੀ ਫਿਲਟਰ ਸੈਟਅੱਪ", subtitle: "ਕਦਮ 7",
      desc: "ਹਰ ਵਾਊਚਰ ਐਂਟਰੀ ਇਨਪੁੱਟ ਵਿੱਚ ਕਿਹੜੇ ਗਰੁੱਪ ਅਤੇ ਸਬ-ਗਰੁੱਪ ਦਿਖਣ, ਇਹ ਫਿਲਟਰ ਕਰੋ",
      details: [
        "📋 ਕੋਈ ਵੀ ਵਾਊਚਰ ਖੋਲ੍ਹੋ: ਵਿਕਰੀ / ਖਰੀਦ / ਰਸੀਦ / ਭੁਗਤਾਨ / ਜਰਨਲ",
        "🔍 ਕਿਸੇ ਵੀ ਇਨਪੁੱਟ ਫੀਲਡ 'ਤੇ ਸਰਚ ਫਿਲਟਰ ਕਲਿੱਕ ਕਰੋ",
        "✅ ਚੁਣੋ ਕਿ ਕਿਹੜੇ ਗਰੁੱਪ ਜਾਂ ਸਬ-ਗਰੁੱਪ ਦੀਆਂ ਕੀਮਤਾਂ ਦਿਖਾਉਣੀਆਂ ਹਨ",
      ],
      note: "💡 ਇਸ ਨਾਲ ਸਹੀ ਖਾਤਾ ਬਹੀ ਜਲਦੀ ਮਿਲਦੀ ਹੈ — ਸਾਰੇ ਖਾਤਿਆਂ ਵਿੱਚ ਸਕ੍ਰੋਲ ਨਹੀਂ ਕਰਨਾ ਪੈਂਦਾ!",
      color: "#f59e0b", bgColor: "#fffbeb", borderColor: "#fde68a"
    },
    {
      icon: "📒", title: "ਜਰਨਲ ਆਈਟਮ ਸੈਟਿੰਗਜ਼", subtitle: "ਕਦਮ 8",
      desc: "ਜਰਨਲ ਵਾਊਚਰ ਵਿੱਚ ਆਈਟਮਾਂ, ਸਟਾਕ ਅਤੇ ਮਾਤਰਾ ਕਾਲਮ ਚਾਲੂ ਕਰੋ",
      details: [
        "⚙️ ਸੈਟਿੰਗਜ਼ → ਹੋਰ ਵਿਕਲਪ (More Options)",
        "✅ ਚਾਲੂ ਕਰੋ: ਜਰਨਲ ਐਂਟਰੀ ਵਿੱਚ ਆਈਟਮਾਂ ਅਤੇ ਸਟਾਕ ਦਿਖਾਓ"
      ],
      note: "💡 ਇਨਵੈਂਟਰੀ ਆਈਟਮਾਂ ਵਾਲੇ ਐਡਜਸਟਮੈਂਟ ਲਈ ਲਾਭਦਾਇਕ!",
      color: "#8b5cf6", bgColor: "#f5f3ff", borderColor: "#ddd6fe"
    },

    {
      icon: "📝", title: "ਰਜਿਸਟਰ ਐਂਟਰੀ", subtitle: "ਕਦਮ 9",
      desc: "ਰੋਜ਼ਾਨਾ ਦੇ ਲੈਣ-ਦੇਣ ਰਿਕਾਰਡ ਕਰਨਾ ਸ਼ੁਰੂ ਕਰੋ",
      details: ["🧾 ਵਾਊਚਰ ਐਂਟਰੀ", "💰 ਰਸੀਦ, ਭੁਗਤਾਨ, ਵਿਕਰੀ, ਖਰੀਦ", "🎉 ਸਾਫਟਵੇਅਰ ਵਰਤਣਾ ਸ਼ੁਰੂ ਕਰੋ!"],
      color: "#6366f1", bgColor: "#eef2ff", borderColor: "#c7d2fe", isLast: true
    },
    {
      icon: "👨‍💼", title: "ਮੈਨੇਜਰ", subtitle: "ਕਦਮ 10",
      desc: "ਮੈਨੇਜਰ ਬਣਾਓ ਅਤੇ ਖਾਸ ਬ੍ਰਾਂਚ ਤੇ ਅਧਿਕਾਰ ਦਿਓ",
      details: ["⚙️ ਸੈਟਿੰਗਜ਼ → ਮੈਨੇਜਰ ਪ੍ਰਬੰਧਨ", "👤 ਮੈਨੇਜਰ ਬਣਾਓ: ਨਾਮ, ਪਾਸਵਰਡ", "🏢 ਬ੍ਰਾਂਚ ਦਿਓ: ABC Company", "🔒 ਅਧਿਕਾਰ ਦਿਓ: ਐਂਟਰੀ, ਡੇ ਬੁੱਕ"],
      color: "#ec4899", bgColor: "#fdf2f8", borderColor: "#fbcfe8"
    },
  ]
};

function formatContent(content, activeTab) {
  const lines = content.split('\n');
  const elements = [];
  let currentSection = [];
  let isInPart3 = false;
  let part3Content = { assets: [], liabilities: [], income: [], expense: [] };
  let currentType = null;

  const flushSection = (key) => {
    if (currentSection.length > 0) {
      elements.push(<div key={key} style={{ marginBottom: '4px' }}>{currentSection}</div>);
      currentSection = [];
    }
  };

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();

    if (/^(Part|भाग|ਭਾਗ)\s*3/.test(trimmedLine)) {
      isInPart3 = true;
      flushSection(`section-${elements.length}`);
      currentSection.push(
        <div key={`Part-${index}`} style={{ fontWeight: '600', color: '#be8024', marginBottom: '8px', background: '#eff6ff', borderLeft: '4px solid #be8024', padding: '4px 8px', borderRadius: '6px' }}>
          {trimmedLine}
        </div>
      );
      return;
    }

    if (isInPart3 && /^(Part|भाग|ਭਾਗ)\s*4/.test(trimmedLine)) {
      isInPart3 = false;
      flushSection(`section-${elements.length}`);

      const colLabels = {
        assets: activeTab === 'hindi' ? '🟢 संपत्ति' : activeTab === 'panjabi' ? '🟢 ਸੰਪਤੀ' : '🟢 Assets',
        liabilities: activeTab === 'hindi' ? '🔴 दायित्व' : activeTab === 'panjabi' ? '🔴 ਦੇਣਦਾਰੀਆਂ' : '🔴 Liabilities',
        income: activeTab === 'hindi' ? '🔵 आय' : activeTab === 'panjabi' ? '🔵 ਆਮਦਨ' : '🔵 Income',
        expense: activeTab === 'hindi' ? '🟡 व्यय' : activeTab === 'panjabi' ? '🟡 ਖਰਚੇ' : '🟡 Expense',
      };
      const colColors = {
        assets: { bg: '#f0fdf4', text: '#15803d' },
        liabilities: { bg: '#fef2f2', text: '#dc2626' },
        income: { bg: '#eff6ff', text: '#be8024' },
        expense: { bg: '#fefce8', text: '#ca8a04' }
      };

      elements.push(
        <div key="part3-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px', marginBottom: '8px', padding: '0 8px' }}>
          {Object.entries(part3Content).map(([type, items]) => (
            <div key={type} style={{ border: '1px solid #d1d5db', borderRadius: '8px', padding: '8px', background: colColors[type].bg }}>
              <div style={{ fontWeight: '700', color: colColors[type].text, marginBottom: '4px', textAlign: 'center', fontSize: '13px' }}>{colLabels[type]}</div>
              <div style={{ fontSize: '12px' }}>{items}</div>
            </div>
          ))}
        </div>
      );

      part3Content = { assets: [], liabilities: [], income: [], expense: [] };
      currentType = null;

      currentSection.push(
        <div key={`Part-${index}`} style={{ fontWeight: '600', color: '#be8024', marginBottom: '8px', background: '#eff6ff', borderLeft: '4px solid #be8024', padding: '4px 8px', borderRadius: '6px' }}>
          {trimmedLine}
        </div>
      );
      return;
    }

    if (isInPart3) {
      if (/^🟢/.test(trimmedLine)) { currentType = 'assets'; return; }
      else if (/^🔴/.test(trimmedLine)) { currentType = 'liabilities'; return; }
      else if (/^🔵/.test(trimmedLine)) { currentType = 'income'; return; }
      else if (/^🟡/.test(trimmedLine)) { currentType = 'expense'; return; }

      if (currentType && trimmedLine) {
        if (/^[1-9]️⃣/.test(trimmedLine)) {
          part3Content[currentType].push(<div key={`${currentType}-${index}`} style={{ fontWeight: '600', color: '#1f2937', marginTop: '6px' }}>{trimmedLine}</div>);
        } else if (/^\(.*\)$/.test(trimmedLine)) {
          part3Content[currentType].push(<div key={`${currentType}-${index}`} style={{ fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>{trimmedLine}</div>);
        } else if (/^🔹/.test(trimmedLine)) {
          part3Content[currentType].push(<div key={`${currentType}-${index}`} style={{ fontWeight: '600', color: '#374151', marginTop: '4px' }}>{trimmedLine}</div>);
        } else if (/^•/.test(trimmedLine)) {
          part3Content[currentType].push(<li key={`${currentType}-${index}`} style={{ marginLeft: '16px', color: '#374151' }}>{trimmedLine.substring(1).trim()}</li>);
        } else if (/^✔/.test(trimmedLine)) {
          part3Content[currentType].push(<div key={`${currentType}-${index}`} style={{ background: '#fff', borderLeft: '2px solid #22c55e', paddingLeft: '6px', paddingTop: '2px', paddingBottom: '2px', fontSize: '11px', borderRadius: '2px' }}>{trimmedLine}</div>);
        } else if (trimmedLine.includes('Ledgers you can create') || trimmedLine.includes('इसके अंतर्गत') || trimmedLine.includes('ਤੁਸੀਂ ਜੋ') || trimmedLine.includes('ਇਸ ਦੇ ਅਧੀਨ')) {
          part3Content[currentType].push(<div key={`${currentType}-${index}`} style={{ fontSize: '11px', fontWeight: '500', color: '#4b5563', marginTop: '2px' }}>{trimmedLine}</div>);
        } else if (!trimmedLine.match(/^[\s-]+$/)) {
          part3Content[currentType].push(<div key={`${currentType}-${index}`} style={{ fontSize: '11px', color: '#6b7280' }}>{trimmedLine}</div>);
        }
      }
      return;
    }

    // Non-Part3 rendering
    if (/^\d+\..+/.test(trimmedLine)) {
      currentSection.push(<div key={`h2-${index}`} style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', padding: '0 12px' }}>{trimmedLine}</div>);
    } else if (/^(Part|भाग|ਭਾਗ)\s*\d+/.test(trimmedLine) || /^🔹\s*(Part|भाग|ਭਾਗ)/.test(trimmedLine)) {
      flushSection(`section-${elements.length}`);
      currentSection.push(
        <div key={`Part-${index}`} style={{ fontWeight: '600', color: '#be8024', marginBottom: '8px', background: '#eff6ff', borderLeft: '4px solid #be8024', padding: '4px 8px', borderRadius: '6px' }}>
          {trimmedLine}
        </div>
      );
    } else if (/^(Step|चरण|ਕਦਮ)\s*\d+/.test(trimmedLine)) {
      currentSection.push(<div key={`Step-${index}`} style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', padding: '0 12px' }}>{trimmedLine}</div>);
    } else if (/^🔹/.test(trimmedLine)) {
      currentSection.push(<div key={`diamond-${index}`} style={{ fontWeight: '600', color: '#374151' }}>{trimmedLine}</div>);
    } else if (/^👉/.test(trimmedLine)) {
      currentSection.push(
        <div key={`arrow-${index}`} style={{ background: '#f0fdf4', fontSize: '13px', borderLeft: '4px solid #4ade80', borderRadius: '4px' }}>
          <p style={{ color: '#374151', fontWeight: '500', padding: '4px 8px' }}>{trimmedLine}</p>
        </div>
      );
    } else if (/^[🟢🔴🔵🟡]/.test(trimmedLine)) {
      currentSection.push(<div key={`emoji-${index}`} style={{ fontWeight: '700', color: '#374151' }}>{trimmedLine}</div>);
    } else if (/^[1-9]️⃣/.test(trimmedLine)) {
      currentSection.push(<div key={`numbered-${index}`} style={{ fontWeight: '600', color: '#374151' }}>{trimmedLine}</div>);
    } else if (/^•/.test(trimmedLine)) {
      currentSection.push(<li key={`bullet-${index}`} style={{ marginLeft: '80px', fontSize: '13px', color: '#374151' }}>{trimmedLine.substring(1).trim()}</li>);
    } else if (/^[✅✔]/.test(trimmedLine)) {
      currentSection.push(
        <div key={`check-${index}`} style={{ background: '#f0fdf4', borderLeft: '4px solid #4ade80', margin: '4px 0', fontSize: '13px', fontWeight: '500', padding: '4px 8px', borderRadius: '4px' }}>
          <div style={{ color: '#374151', padding: '4px 0' }}>{trimmedLine}</div>
        </div>
      );
    } else if (/^[Q.]/.test(trimmedLine) && trimmedLine.startsWith('Q.')) {
      currentSection.push(
        <div key={`q-${index}`} style={{ color: '#dc2626', margin: '4px 0', fontSize: '13px', fontWeight: '500', padding: '0 8px', borderRadius: '4px' }}>
          <div style={{ padding: '4px 0' }}>{trimmedLine}</div>
        </div>
      );
    } else if (trimmedLine.includes('/') && (trimmedLine.includes('Type') || trimmedLine.includes('प्रकार') || trimmedLine.includes('ਕਿਸਮ') || trimmedLine.includes('Payment') || trimmedLine.includes('Option') || trimmedLine.includes('विकल्प') || trimmedLine.includes('ਵਿਕਲਪ'))) {
      const headers = trimmedLine.split('/').map(h => h.trim());
      currentSection.push(
        <div key={`table-${index}`} style={{ overflowX: 'auto', marginBottom: '4px', padding: '0 12px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d1d5db' }}>
            <thead>
              <tr style={{ background: '#be8024', color: '#fff' }}>
                {headers.map((header, i) => (
                  <th key={i} style={{ border: '1px solid #d1d5db', padding: '4px 8px', fontSize: '13px', textAlign: 'left', fontWeight: '600', width: `${100 / headers.length}%` }}>{header}</th>
                ))}
              </tr>
            </thead>
          </table>
        </div>
      );
    } else if (trimmedLine.includes('/') && !trimmedLine.includes('Type') && !trimmedLine.includes('प्रकार') && !trimmedLine.includes('ਕਿਸਮ') && !trimmedLine.includes('Option') && !trimmedLine.includes('विकल्प') && !trimmedLine.includes('ਵਿਕਲਪ')) {
      const cells = trimmedLine.split('/').map(c => c.trim());
      if (cells.length > 1) {
        currentSection.push(
          <div key={`table-row-${index}`} style={{ overflowX: 'auto', marginBottom: '2px', padding: '0 12px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d1d5db' }}>
              <tbody>
                <tr>
                  {cells.map((cell, i) => (
                    <td key={i} style={{ border: '1px solid #d1d5db', padding: '4px 8px', fontSize: '13px', width: `${100 / cells.length}%` }}>{cell}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        );
      }
    } else if (trimmedLine && !trimmedLine.match(/^[\s-]+$/)) {
      currentSection.push(<p key={`p-${index}`} style={{ marginBottom: '1px', padding: '0 12px', fontSize: '13px', color: '#374151', lineHeight: '1.6' }}>{trimmedLine}</p>);
    } else if (!trimmedLine) {
      currentSection.push(<div key={`space-${index}`} style={{ marginBottom: '2px' }}></div>);
    }
  });

  if (currentSection.length > 0) {
    elements.push(<div key={`section-final`} style={{ marginBottom: '24px' }}>{currentSection}</div>);
  }

  return elements;
}
function StepCard({ step }) {
  return (
    <div
      style={{
        background: "#fff", borderRadius: "16px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        border: `2px solid ${step.borderColor}`,
        overflow: "hidden", position: "relative",
        transition: "transform 0.2s, box-shadow 0.2s"
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "0 12px 32px rgba(0,0,0,0.15)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)";
      }}
    >
      {/* Top color bar */}
      <div style={{ height: "5px", background: step.color }} />

      <div style={{ padding: "20px" }}>

        {/* Subtitle badge + Icon */}
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", marginBottom: "12px"
        }}>
          <span style={{
            display: "inline-flex", alignItems: "center",
            padding: "3px 10px", borderRadius: "20px",
            fontSize: "11px", fontWeight: "700",
            textTransform: "uppercase", letterSpacing: "0.05em",
            background: step.color, color: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
          }}>
            {step.subtitle}
          </span>
          <div style={{
            width: "44px", height: "44px", background: step.bgColor,
            borderRadius: "12px", display: "flex",
            alignItems: "center", justifyContent: "center", fontSize: "24px"
          }}>
            {step.icon}
          </div>
        </div>

        {/* Title */}
        <h4 style={{ fontSize: "16px", fontWeight: "700", color: "#1e293b", margin: "0 0 8px 0" }}>
          {step.title}
        </h4>

        {/* Desc */}
        <p style={{ fontSize: "13px", color: "#64748b", margin: "0 0 12px 0", lineHeight: "1.5" }}>
          {step.desc}
        </p>

        {/* Detail rows */}
        <div style={{ background: step.bgColor, borderRadius: "10px", padding: "12px" }}>
          {step.details.map((detail, dIndex) => (
            <div key={dIndex} style={{
              display: "flex", alignItems: "flex-start", gap: "8px",
              marginBottom: "8px", fontSize: "13px"
            }}>
              <span style={{
                flexShrink: 0, width: "22px", height: "22px",
                borderRadius: "50%", background: "#fff",
                boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: "13px"
              }}>
                {detail.charAt(0)}
              </span>
              <span style={{ color: "#374151", fontWeight: "500" }}>
                {detail.substring(2)}
              </span>
            </div>
          ))}

          {/* ── Optional inline table (only for Step 2) ── */}
          {step.tableHead && step.tableRows && (
            <div style={{
              marginTop: "8px",
              borderRadius: "8px",
              overflow: "hidden",
              border: `1px solid ${step.borderColor}`
            }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                <thead>
                  <tr style={{ background: step.color }}>
                    {step.tableHead.map((h, i) => (
                      <th key={i} style={{
                        padding: "6px 10px", color: "#fff",
                        fontWeight: "700", textAlign: "left",
                        borderRight: i < step.tableHead.length - 1
                          ? "1px solid rgba(255,255,255,0.3)" : "none"
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {step.tableRows.map((row, ri) => (
                    <tr key={ri} style={{
                      background: ri % 2 === 0 ? "#fff" : step.bgColor
                    }}>
                      {row.map((cell, ci) => (
                        <td key={ci} style={{
                          padding: "6px 10px",
                          color: "#1e3a5f",
                          fontWeight: ci === 0 ? "600" : "400",
                          borderTop: `1px solid ${step.borderColor}`,
                          borderRight: ci < row.length - 1
                            ? `1px solid ${step.borderColor}` : "none"
                        }}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Optional note (only for Step 2) ── */}
          {step.note && (
            <div style={{
              marginTop: "10px",
              display: "flex", alignItems: "flex-start", gap: "8px",
              fontSize: "12px"
            }}>
              <span style={{
                flexShrink: 0, width: "22px", height: "22px",
                borderRadius: "50%", background: "#fff",
                boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: "13px"
              }}>💡</span>
              <span style={{ color: "#0369a1", fontWeight: "500", lineHeight: "1.5" }}>
                {step.note}
              </span>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}



function SetupFlow({ activeTab }) {
  const steps = setupStepsData[activeTab] || setupStepsData.assets;

  const titles = {
    assets: "Complete Setup Guide",
    hindi: "पूर्ण सेटअप गाइड",
    panjabi: "ਪੂਰੀ ਸੈਟਅੱਪ ਗਾਈਡ"
  };
  const subtitles = {
    assets: "Follow these 10 simple steps to configure",
    hindi: "इन 10 सरल चरणों का पालन करें",
    panjabi: "ਇਨ੍ਹਾਂ 10 ਸਰਲ ਕਦਮਾਂ ਦੀ ਪਾਲਣਾ ਕਰੋ"
  };
  const completeMsgs = {
    assets: "You're all set! Start using the software.",
    hindi: "आप तैयार हैं! सॉफ्टवेयर का उपयोग शुरू करें।",
    panjabi: "ਤੁਸੀਂ ਤਿਆਰ ਹੋ! ਸਾਫਟਵੇਅਰ ਵਰਤਣਾ ਸ਼ੁਰੂ ਕਰੋ।"
  };

  return (
    <div style={{
      background: "linear-gradient(135deg, #f8fafc, #eff6ff, #eef2ff)",
      borderRadius: "24px", padding: "32px",
      boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
      border: "1px solid rgba(255,255,255,0.6)",
      marginTop: "24px"
    }}>

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: "72px", height: "72px",
          background: "linear-gradient(135deg, #3b82f6, #a855f7, #ec4899)",
          borderRadius: "18px",
          boxShadow: "0 8px 24px rgba(99,102,241,0.4)",
          marginBottom: "16px", fontSize: "36px"
        }}>🚀</div>
        <h3 style={{ fontSize: "26px", fontWeight: "700", color: "#1e293b", margin: "0 0 8px 0" }}>
          {titles[activeTab]}
        </h3>
        <p style={{ color: "#64748b", fontSize: "15px", margin: 0 }}>
          {subtitles[activeTab]}
        </p>
      </div>

      {/* All cards in one clean grid — no separate component needed */}
      <div style={{
        maxWidth: "", margin: "0 auto",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: "16px"
      }}>
        {steps.map((step, index) => (
          <StepCard key={index} step={step} />
        ))}
      </div>

      {/* Completion Message */}
      <div style={{ marginTop: "32px", textAlign: "center" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: "10px",
          background: "linear-gradient(135deg, #dcfce7, #d1fae5)",
          color: "#166534", padding: "12px 28px", borderRadius: "40px",
          fontWeight: "700", fontSize: "16px",
          boxShadow: "0 4px 16px rgba(34,197,94,0.2)",
          border: "2px solid #bbf7d0"
        }}>
          <span style={{ fontSize: "22px" }}>🎉</span>
          <span>{completeMsgs[activeTab]}</span>
          <span style={{ fontSize: "22px" }}>🚀</span>
        </div>
      </div>
    </div>
  );
}

// ─── WebGuideBook (Main) ──────────────────────────────────────────────────────

export default function WebGuideBook() {
  const typeKeys = Object.keys(typeDetailsData);
  const [activeTab, setActiveTab] = useState(typeKeys[0]);
  const details = typeDetailsData[activeTab];

  return (
    <div style={{ minHeight: "100vh", background: "#f1f5f9" }}>
      <div style={{ margin: "0 auto", paddingBottom: "8px", width: "100%", maxWidth: "" }}>

        {/* Header */}
        <div style={{
          padding: "8px 16px", borderRadius: "8px",
          background: "#be8024", color: "#fff",
          boxShadow: "0 4px 12px rgba(37,99,235,0.3)",
          position: "sticky", top: 0, zIndex: 50
        }}>
          <div style={{ fontWeight: "600", fontSize: "18px", textAlign: "center" }}>
            📚 Guide Book / गाइड बुक
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: "flex", marginBottom: "12px", gap: "8px",
          position: "sticky", top: "44px", background: "#f1f5f9",
          padding: "12px 16px", zIndex: 40
        }}>
          {typeKeys.map((key) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                padding: "6px 20px", fontSize: "14px", fontWeight: "500",
                borderRadius: "8px", cursor: "pointer", transition: "all 0.2s",
                border: activeTab === key ? "none" : "1px solid #d1d5db",
                background: activeTab === key ? "#be8024" : "#fff",
                color: activeTab === key ? "#fff" : "#374151",
                boxShadow: activeTab === key ? "0 4px 12px rgba(37,99,235,0.3)" : "none"
              }}
            >
              {typeDetailsData[key].title}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{
          padding: "16px", background: "#fff",
          borderRadius: "8px", boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
          border: "1px solid #e5e7eb", margin: "0 16px"
        }}>
          <div>{formatContent(details.content, activeTab)}</div>
          <SetupFlow activeTab={activeTab} />
        </div>

      </div>
    </div>
  );
}

